import { UsageOverview } from '~api';
import { type Vals } from '~lib';

/**
 * Pure logic for the admin usage overview page (`/admin/usage`, H2-266): the shareable URL state,
 * the range presets, and the shaping of the backend's daily series into what the charts draw.
 * Kept out of the route so every rule — the default range, what an invalid URL falls back to, when
 * the internal toggle must switch off — is pinned by a unit test.
 *
 * Every day here is a UTC calendar day, `YYYY-MM-DD`: the ledger buckets by `toDate(ts)` in UTC,
 * so "today" is the UTC date, not the viewer's.
 */

export const RangePresets = {
  Today: 'today',
  Last7Days: '7d',
  Last30Days: '30d',
  Last90Days: '90d',
} as const;
export type TRangePreset = Vals<typeof RangePresets>;

export const DEFAULT_RANGE_PRESET: TRangePreset = RangePresets.Last30Days;

/** @desc Days each preset spans, inclusive of today. */
const PRESET_DAYS: Record<TRangePreset, number> = {
  [RangePresets.Today]: 1,
  [RangePresets.Last7Days]: 7,
  [RangePresets.Last30Days]: 30,
  [RangePresets.Last90Days]: 90,
};

export const RANGE_PRESET_LABELS: Record<TRangePreset, string> = {
  [RangePresets.Today]: 'Today',
  [RangePresets.Last7Days]: 'Last 7 days',
  [RangePresets.Last30Days]: 'Last 30 days',
  [RangePresets.Last90Days]: 'Last 90 days',
};

const PRESET_VALUES = new Set<string>(Object.values(RangePresets));
const isPreset = (value: unknown): value is TRangePreset =>
  typeof value === 'string' && PRESET_VALUES.has(value);

/**
 * @desc The URL spelling of the {@link UsageOverview.NO_ORGANISATION} bucket. The ledger's own
 * value is the empty string, which neither a search param nor a select option carries cleanly;
 * WorkOS organisation ids all start `org_`, so `none` can never collide with one.
 */
export const NO_ORGANISATION_PARAM = 'none';

/**
 * @desc The page's shareable state. Every field is optional and omitted at its default, so the
 * bare `/admin/usage` URL is the default view: the last 30 days, every customer organisation,
 * internal organisations excluded.
 *
 * - `range` — a preset; ignored when a valid custom `from`/`to` pair is present.
 * - `from`/`to` — a custom inclusive range of UTC days; only honoured as a pair.
 * - `org` — organisation ids, {@link NO_ORGANISATION_PARAM} for the no-organisation bucket.
 * - `user` — one portal user id.
 * - `includeInternal` — present (`true`) only when the exclusion toggle is off.
 */
export type UsageSearch = {
  range?: TRangePreset;
  from?: string;
  to?: string;
  org?: string[];
  user?: string;
  includeInternal?: true;
};

const isDay = (value: unknown): value is string =>
  typeof value === 'string' &&
  UsageOverview.DAY_PATTERN.test(value) &&
  !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) &&
  new Date(`${value}T00:00:00Z`).toISOString().startsWith(value);

const stringsOf = (value: unknown): string[] => {
  const values = Array.isArray(value) ? value : [value];
  return values.filter((v): v is string => typeof v === 'string' && v.length > 0);
};

/**
 * @desc The router's `validateSearch`: keeps what is valid and drops the rest, never throws — a
 * hand-edited or stale link degrades to the default view rather than an error page. A custom range
 * needs both ends, each a real calendar day, `from` not after `to`.
 */
export const parseUsageSearch = (raw: Record<string, unknown>): UsageSearch => {
  const search: UsageSearch = {};
  if (isDay(raw.from) && isDay(raw.to) && raw.from <= raw.to) {
    search.from = raw.from;
    search.to = raw.to;
  } else if (isPreset(raw.range) && raw.range !== DEFAULT_RANGE_PRESET) {
    search.range = raw.range;
  }
  const org = [...new Set(stringsOf(raw.org))].slice(0, UsageOverview.MAX_ORG_FILTER);
  if (org.length > 0) search.org = org;
  const [user] = stringsOf(raw.user);
  if (user) search.user = user;
  if (raw.includeInternal === true || raw.includeInternal === 'true') search.includeInternal = true;
  return search;
};

/** @desc The UTC calendar day of an instant, `YYYY-MM-DD`. */
export const utcDay = (at: Date): string => at.toISOString().slice(0, 10);

/** @desc A UTC day shifted by whole days. */
export const addDays = (day: string, days: number): string => {
  const at = new Date(`${day}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return utcDay(at);
};

/** @desc Days from `from` to `to` inclusive (1 for a single day). */
export const daysInRange = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;

/**
 * @desc The earliest day a custom range may start: the ledger's retention horizon, computed the
 * way the backend's `overviewRange` refuses it (today, UTC, minus `RETENTION_MONTHS` months).
 */
export const retentionStartDay = (now: Date): string => {
  const at = new Date(`${utcDay(now)}T00:00:00Z`);
  at.setUTCMonth(at.getUTCMonth() - UsageOverview.RETENTION_MONTHS);
  return utcDay(at);
};

export type ResolvedRange = { from: string; to: string; preset: TRangePreset | 'custom' };

/** @desc A preset as an inclusive range ending today (UTC). */
export const presetRange = (preset: TRangePreset, now: Date): ResolvedRange => {
  const to = utcDay(now);
  return { from: addDays(to, 1 - PRESET_DAYS[preset]), to, preset };
};

/** @desc The range a search names: its custom pair when present, else its preset or the default. */
export const resolveRange = (search: UsageSearch, now: Date): ResolvedRange =>
  search.from && search.to
    ? { from: search.from, to: search.to, preset: 'custom' }
    : presetRange(search.range ?? DEFAULT_RANGE_PRESET, now);

/** @desc The `admin.usageOverview` input a search names. */
export const overviewInput = (search: UsageSearch, now: Date): UsageOverview.Input => {
  const { from, to } = resolveRange(search, now);
  return {
    from,
    to,
    ...(search.org?.length ? { orgIds: search.org.map(orgIdFromParam) } : {}),
    ...(search.user ? { userId: search.user } : {}),
    excludeInternal: !search.includeInternal,
  };
};

export const orgIdFromParam = (param: string): string =>
  param === NO_ORGANISATION_PARAM ? UsageOverview.NO_ORGANISATION : param;
export const orgParamFromId = (orgId: string): string =>
  orgId === UsageOverview.NO_ORGANISATION ? NO_ORGANISATION_PARAM : orgId;

const DAY_SHORT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'short',
});
const DAY_LONG = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const DAY_MEDIUM = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/** @desc An axis label: `1 Oct`. */
export const formatDayShort = (day: string): string =>
  DAY_SHORT.format(new Date(`${day}T00:00:00Z`));
/** @desc A tooltip or table label: `Thu, 1 Oct 2026`. */
export const formatDayLong = (day: string): string => DAY_LONG.format(new Date(`${day}T00:00:00Z`));

/** @desc The range as the picker's button reads: `2 Sep 2026 – 1 Oct 2026`, or one day alone. */
export const formatRange = ({ from, to }: { from: string; to: string }): string => {
  const start = DAY_MEDIUM.format(new Date(`${from}T00:00:00Z`));
  return from === to ? start : `${start} – ${DAY_MEDIUM.format(new Date(`${to}T00:00:00Z`))}`;
};

export const rangeLabel = (range: ResolvedRange): string =>
  range.preset === 'custom' ? 'Custom range' : RANGE_PRESET_LABELS[range.preset];

/** @desc A measure the page charts, with how it is titled and described. */
export type ChartMeasure = keyof UsageOverview.Measures;

export const CHART_MEASURES: ReadonlyArray<{
  measure: ChartMeasure;
  title: string;
  description: string;
}> = [
  {
    measure: 'activeConversations',
    title: 'Sessions',
    description: 'Distinct conversations with a recorded model call that day',
  },
  {
    measure: 'questions',
    title: 'Questions',
    description: 'Distinct turns the model answered at least one call for',
  },
  { measure: 'activeUsers', title: 'Users', description: 'Distinct users active that day' },
  {
    measure: 'activeOrgs',
    title: 'Organisations',
    description: 'Distinct organisations active that day (no-organisation activity is not one)',
  },
  { measure: 'credits', title: 'Credits', description: 'Weighted credits charged that day' },
];

export type SeriesPoint = { day: string; value: number };

/** @desc One measure of the daily series, in day order, as the chart draws it. */
export const seriesOf = (days: readonly UsageOverview.Day[], measure: ChartMeasure) =>
  [...days]
    .sort((a, b) => a.day.localeCompare(b.day))
    .map((day): SeriesPoint => ({ day: day.day, value: Math.max(0, day[measure]) }));

/**
 * @desc A clean axis ceiling at or above `max` — 1, 2 or 5 times a power of ten — and the ticks
 * from zero to it, so gridlines land on round numbers. An all-zero series still gets a 0–1 axis,
 * so the empty columns sit on a baseline rather than dividing by zero.
 */
export const axisTicks = (max: number, steps = 4): number[] => {
  if (!Number.isFinite(max) || max <= 0) return [0, 1];
  const rough = max / steps;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= rough) ?? rough;
  // Counts never want a fractional gridline: a series peaking at 3 users steps by 1, not 0.75.
  const whole = Math.max(1, step);
  const ticks: number[] = [];
  for (let tick = 0; tick < max + whole; tick += whole) {
    ticks.push(tick);
    if (tick >= max) break;
  }
  return ticks;
};

/**
 * @desc Which day indexes get an x-axis label: at most `max`, evenly spaced, always the first and
 * last, so a 90-day axis is not a solid block of dates.
 */
export const labelledDayIndexes = (count: number, max = 6): number[] => {
  if (count <= 0) return [];
  if (count <= max) return Array.from({ length: count }, (_, i) => i);
  const step = (count - 1) / (max - 1);
  return [...new Set(Array.from({ length: max }, (_, i) => Math.round(i * step)))];
};

/**
 * @desc The names the "exclude internal" hint lists: each excluded organisation by its directory
 * name (an id WorkOS no longer lists is shown as its id), and the no-organisation bucket last.
 */
export const excludedNames = (
  excludedOrgIds: readonly string[],
  organisations: readonly UsageOverview.Organisation[],
): string[] => {
  const byId = new Map(organisations.map((org) => [org.id, org.name]));
  const named = excludedOrgIds
    .filter((id) => id !== UsageOverview.NO_ORGANISATION)
    .map((id) => byId.get(id) ?? id)
    .sort((a, b) => a.localeCompare(b, 'en-GB'));
  return excludedOrgIds.includes(UsageOverview.NO_ORGANISATION)
    ? [...named, UsageOverview.NO_ORGANISATION_NAME]
    : named;
};

/** @desc An organisation's display name, the no-organisation bucket included. */
export const organisationName = (
  orgId: string,
  organisations: readonly UsageOverview.Organisation[],
): string =>
  orgId === UsageOverview.NO_ORGANISATION
    ? UsageOverview.NO_ORGANISATION_NAME
    : (organisations.find((org) => org.id === orgId)?.name ?? orgId);

/**
 * @desc Whether narrowing to these organisations needs the internal exclusion switched off. The
 * backend intersects the filter with the exclusion, so an internal organisation (or the
 * no-organisation bucket) selected with the toggle on reads as zero — never what the admin meant.
 */
export const selectionNeedsInternal = (
  orgParams: readonly string[],
  organisations: readonly UsageOverview.Organisation[],
): boolean => {
  const internal = new Set(organisations.filter((org) => org.internal).map((org) => org.id));
  return orgParams.some((param) => param === NO_ORGANISATION_PARAM || internal.has(param));
};

/** @desc True when the range recorded nothing at all, which the page states instead of charting. */
export const isEmptyOverview = (summary: UsageOverview.Measures): boolean =>
  summary.credits === 0 && summary.questions === 0 && summary.activeConversations === 0;

/** @desc A change to the page's search: `undefined` clears a field back to its default. */
export type UsageSearchPatch = {
  [K in keyof UsageSearch]?: UsageSearch[K] | undefined;
};

/**
 * @desc Applies a patch to the current search and normalises it the way `parseUsageSearch` would,
 * so a default is never written into the URL. Choosing a preset drops any custom pair, and a custom
 * pair drops the preset — the two never coexist.
 */
export const withSearch = (current: UsageSearch, patch: UsageSearchPatch): UsageSearch => {
  const merged: Record<string, unknown> = { ...current, ...patch };
  if ('range' in patch) {
    delete merged.from;
    delete merged.to;
  }
  if ('from' in patch || 'to' in patch) delete merged.range;
  return parseUsageSearch(merged);
};
